import { RadioGroup } from '../../../../components/inputs/RadioGroup';
import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { repositoryApi } from '../repositoryApi';
import { RepositoryPermissions, type RepositoryItemListItem } from '../repositoryTypes';
import { sampleFolder } from '../utils/sampleFolder';

/**
 * Create or edit one repository folder.
 *
 * The parent is **not** a field: a folder is created in the folder the reader is standing in,
 * which they chose by navigating there. Offering a picker would ask them to answer a question
 * the breadcrumb already answers — and this module's whole shape is that the breadcrumb
 * carries position.
 */
export function FolderModal({
  open,
  editing,
  parentId,
  parentName,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: RepositoryItemListItem | null;
  /** The folder being created into. Null is the root. Ignored when editing. */
  parentId: string | null;
  /** What to call that folder on the form. Null at the root. */
  parentName: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [folderCode, setFolderCode] = useState(editing?.folderCode ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');

  /**
   * GENERAL ACCESS — what everyone in the organization gets here without being named.
   *
   * **Private by default**, which is the reference's own default and the cautious one: a reader
   * who does not think about it gets the restrictive answer. On an EDIT it seeds from the
   * record, where the answer already exists.
   *
   * It is load-bearing rather than a label: the server resolves every read against it, so this
   * control decides who can see everything filed under the folder — and, for anything but None,
   * what they can DO there.
   */
  const [publicPermissions, setPublicPermissions] = useState<RepositoryPermissions>(
    editing?.publicPermissions ?? RepositoryPermissions.None);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const valid = name.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      if (editing) {
        // No folder code and no parent on the update: a reference number is built from the
        // codes above it and is permanent, so changing one would make the tree disagree with
        // numbers already issued.
        await repositoryApi.updateFolder(editing.id, {
          name: name.trim(),
          description: description.trim() || null,
        });
      } else {
        await repositoryApi.createFolder({
          parentId,
          name: name.trim(),
          folderCode: folderCode.trim() || null,
          publicPermissions,
          description: description.trim() || null,
        });
      }

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save the folder.', 0);

      setError(failure);

      // Say which field, show it, and put focus on it.
      focusValidationError(failure, { idPrefix: 'repository-folder' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit folder' : 'New folder'}
      size="lg"
      id="repository-folder-modal"
      autogenerate={{
        onGenerate: () => {
          const generated = sampleFolder();
          setName(generated.name);
          setDescription(generated.description);
          // The folder code is deliberately untouched — see sampleFolder.
        },
        disabled: saving,
      }}
      confirm={{
        id: 'repository-folder-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="repository-folder-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="repository-folder-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {editing === null ? (
          <p className="text-sm text-slate-600">
            Creating in <span className="font-medium text-slate-800">{parentName ?? 'the repository root'}</span>.
          </p>
        ) : null}

        <TextField
          id="repository-folder-name"
          label="Name"
          required
          description="What the folder is called. Held once among the live folders here."
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={260}
          disabled={saving}
        />

        {/*
          Create only, and permanent once set — which is why it is absent on an edit rather
          than disabled there. A reference number is built from every code between a document
          and the root, so changing one after documents exist beneath it would make the tree
          disagree with numbers that have already been issued and quoted elsewhere.
        */}
        {editing === null ? (
          <TextField
            id="repository-folder-folder-code"
            label="Folder code"
            description="Optional, and permanent once set. A code makes this a controlled-document folder: everything filed inside it is issued a reference number built from this code — L1, L2, L4. Leave it empty for a folder that just holds files."
            value={folderCode}
            onChange={(e) => setFolderCode(e.target.value.toUpperCase())}
            maxLength={16}
            disabled={saving}
          />
        ) : null}

        {/*
          VISIBILITY — captured from GRC's Create Folder form, which asks Private or Public.

          A `<RadioGroup>` rather than the reference's two buttons: this is one question with
          two answers, which is what that control is, and a pair of toggle buttons would be a
          second shape for a thing the shared inputs already have.

          **The help text says what it ACTUALLY does, and the reference's does not fit.** GRC's
          reads "only admins and users you specifically add can access this folder". Half of
          that is true here and half is not: a private folder IS closed to everyone but its
          creator and a holder of `repository.read-all` — which is this product's answer to
          "admins", there being no admin concept, only keys — and "users you specifically add" is
          the Share dialog, which landed on 19 Sep 2026. Both halves are true now, so the copy
          says so and points at where the second one is done: this form asks the question once,
          on create, and Share is where it is changed afterwards.

          Create only HERE. Changing general access after documents are filed under it
          re-classifies everything inside, so it is its own operation behind its own key —
          the Share dialog, under `repository.share`. This form carries `repository.create`,
          which is not the same decision.
        */}
        {editing === null ? (
          <RadioGroup
            id="repository-folder-general-access"
            label="General access"
            required
            value={String(publicPermissions)}
            onChange={(value) => setPublicPermissions(Number(value))}
            options={[
              {
                value: String(RepositoryPermissions.None),
                label: 'Private',
                description: 'Only you, and anyone you share it with.',
              },
              {
                value: String(RepositoryPermissions.View),
                label: 'Anyone in the organization can view',
                description: 'They can open what is in here. Only you can change it.',
              },
              {
                value: String(RepositoryPermissions.All & ~RepositoryPermissions.Share),
                label: 'Anyone in the organization can edit',
                description: 'They can also file into it and change what is here.',
              },
            ]}
            description="Who gets access without being named. Named people are added afterwards from Share. A CONFIDENTIAL document is refused into a folder that is not private — the server settles that, not this form."
            disabled={saving}
          />
        ) : null}

        <TextField
          id="repository-folder-description"
          label="Description"
          description="Optional note for whoever maintains this part of the tree."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
